import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import GameLayout from './pages/Game/Game';
import Main from './pages/Main/Main';
import Login from 'pages/Login/Login';
import PrivateRoute from 'services/Account/PrivateRoute';
import SetupBoard from './pages/SetupBoard/SetupBoard';
import Rooms from 'pages/Rooms/Rooms';
import Matchmaking from 'pages/Matchmaking/Matchmaking';
import ChooseUsername from 'pages/ChooseUsername/ChooseUsername';
import Landing from 'pages/Landing/Landing';
import { Analytics } from "@vercel/analytics/react";
import { BoldNavbar } from 'ui';
import { supabase, getSession } from './services/Account/Supabase';
import SignupPage from 'pages/SignUp/SignUp';

// Create Session Context
export const SessionContext = createContext<{
  session: { user: { id: string; username?: string; email?: string } } | null;
  setSession: React.Dispatch<React.SetStateAction<any>>;
}>({ session: null, setSession: () => {} });

const App: React.FC = () => {
  const [session, setSession] = useState<{ user: { id: string; username?: string; email?: string } } | null>(null);

  useEffect(() => {
    document.title = 'Deck Chess!';

    const fetchSession = async () => {
      const userSession = await getSession();
      if (!userSession?.user) {
        setSession(null);
        return;
      }

      try {
        // Fetch the username from Supabase
        const { data, error } = await supabase
          .from("user")
          .select("username")
          .eq("uid", userSession.user.id)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("Error fetching username:", error);
        }

        setSession({
          user: {
            id: userSession.user.id,
            email: userSession.user.email ?? "No EMAIL",
            username: data?.username ?? null, // Set username if found
          },
        });
      } catch (err) {
        console.error("Unexpected error fetching user data:", err);
      }
    };

    fetchSession();

    // Listen for authentication changes (login/logout)
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (newSession?.user) {
        fetchSession(); // Re-fetch username when session changes
      } else {
        setSession(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <SessionContext.Provider value={{ session, setSession }}>
      <div className="rootdiv">
        <BrowserRouter>

        
          <BoldNavbar
            session={session}
            onLogout={async () => {
              await supabase.auth.signOut();
              setSession(null);
            }}
          />

          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/game" element={<PrivateRoute element={<GameLayout />} />} />
            <Route path="/home" element={<PrivateRoute element={<Main />} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/arrange-board" element={<PrivateRoute element={<SetupBoard />} />} />
            <Route path="/rooms" element={<Rooms />} />
            <Route path="/matchmaking" element={<PrivateRoute element={<Matchmaking />} />} />
            <Route path="/choose-username" element={<PrivateRoute element={<ChooseUsername />} />} />
          </Routes>
        </BrowserRouter>
        <Analytics />
        <div className="app-bg"></div>
      </div>
    </SessionContext.Provider>
  );
};

export default App;
